"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Phone, Mail, Loader2, ArrowLeft, CheckCircle, Shield } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { installAuthSession, fetchAuthWithConversation } from "@/lib/auth/client-session";
import { beginAuthTransition } from "@/lib/auth/transition";
import { waitForAnonymousSession } from "@/lib/auth/anonymous-session";
import { JUST_LOGGED_IN_KEY, AUTH_REDIRECT_PATH_KEY } from "@/lib/auth";
import { useTenant } from "@/lib/tenant";
import { strings } from "@/lib/strings";
import { routes } from "@/lib/routes";
import { getSafeReturnPath } from "@/lib/auth/return-destination";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { OtpInput } from "@/components/auth/OtpInput";
import { useOtpAutoSubmit } from "@/components/auth/useOtpAutoSubmit";

type LoginStep =
  | "method"
  | "phone-input"
  | "phone-verify"
  | "email-otp-input"
  | "email-otp-verify"
  | "success";

function markJustLoggedIn() {
  try {
    localStorage.setItem(JUST_LOGGED_IN_KEY, "1");
  } catch {}
}

export interface AuthFlowProps {
  /** Called on successful authentication */
  onSuccess?: () => void;
  /** Custom title (default: "Anmelden oder registrieren") */
  title?: string;
  /** Custom subtitle for method step */
  subtitle?: string;
  /** Pre-fill email */
  initialEmail?: string;
  /** Pre-fill phone */
  initialPhone?: string;
  /** Layout variant: "modal" reloads page on success, "page" redirects to home */
  variant?: "modal" | "page";
  /** Private link to revisit after successful authentication. */
  returnTo?: string;
}

/** How long the success panel shows before moving on. */
const SUCCESS_PANEL_MS = 1500;
/** Longer when the panel carries the new-account hint, so it can be read. */
const NEW_ACCOUNT_HINT_MS = 6000;

export function AuthFlow({
  onSuccess,
  title,
  subtitle,
  initialEmail,
  initialPhone,
  variant = "modal",
  returnTo,
}: AuthFlowProps) {
  const [step, setStep] = useState<LoginStep>("method");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);
  const [emailOtpEmail, setEmailOtpEmail] = useState("");
  const [emailOtpCode, setEmailOtpCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newAccount, setNewAccount] = useState(false);

  const tenant = useTenant();
  const t = strings;
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []);
  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Pre-fill when provided
  useEffect(() => {
    if (initialEmail) setEmailOtpEmail(initialEmail);
    if (initialPhone) setPhoneNumber(initialPhone);
  }, [initialEmail, initialPhone]);

  /**
   * Shared post-verification handler for both phone + email OTP flows.
   *
   * - Shows the success panel for ~1.5s so the user gets visible confirmation.
   * - Calls `router.refresh()` to invalidate the RSC cache: without this, the
   *   server component tree (including the navbar) is still rendered with
   *   `user=null` from the original request and keeps showing "Anmelden"
   *   until a hard reload.
   * - Uses `router.push(routes.home)` for the page variant and `window.location.reload()`
   *   for modal so the caller's layout re-reads cookies with the new session.
   *
   * Anonymous (guest) users are a special case: a verified guest who logs in
   * stays anonymous (no auto-upgrade). The navbar intentionally keeps showing
   * "Anmelden" for anonymous sessions (guest = no account), so we must land them
   * on a page that itself signals success — their guest-framed overview (their
   * entities + a "Gastzugang" banner) — rather than the homepage, where nothing
   * would show the login worked. This needs a FULL navigation: the cookie-based
   * `@supabase/ssr` browser client only re-reads the session on load, and a
   * `Set-Cookie` from the verify fetch does not fire `onAuthStateChange`, so
   * `router.push` would leave the client auth context stale (the guest chrome
   * keys on the client `isAnonymous`).
   */
  const finalizeLoginSuccess = useCallback(
    (isAnonymousUser = false, isNewAccount = false) => {
      setStep("success");
      setNewAccount(isNewAccount);
      markJustLoggedIn();
      // A phone login that CREATED an account may be someone looking for their
      // existing one — e.g. with a number that is no longer its login (ARCH-055).
      // Leave time to read the hint before moving on.
      const delay = isNewAccount ? NEW_ACCOUNT_HINT_MS : SUCCESS_PANEL_MS;

      if (returnTo) {
        const destination = getSafeReturnPath(returnTo, window.location.origin);
        setTimeout(() => {
          onSuccess?.();
          // Re-read the verified cookie session in both the page and auth context.
          window.location.assign(destination);
        }, delay);
        return;
      }

      if (isAnonymousUser) {
        setTimeout(() => {
          onSuccess?.();
          window.location.assign(routes.dashboard);
        }, delay);
        return;
      }

      // Refresh the RSC tree NOW so the navbar re-renders with the new session
      // while the success panel is still on screen. This avoids the stale
      // "Anmelden" nav button showing up on the destination page.
      router.refresh();

      setTimeout(() => {
        onSuccess?.();
        if (variant === "page") {
          router.push(routes.home);
        } else {
          window.location.reload();
        }
      }, delay);
    },
    [router, variant, onSuccess, returnTo]
  );

  // Phone login handlers
  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/phone/send-login-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phoneNumber }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Code konnte nicht gesendet werden");
      }

      setPhoneNumber(data.phone);
      setStep("phone-verify");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleOtpVerifyAuto();
  };

  const handleOtpVerifyAuto = useCallback(async () => {
    if (isLoading) return;
    setError(null);
    setIsLoading(true);
    const endAuthTransition = beginAuthTransition();

    try {
      await waitForAnonymousSession();
      const response = await fetchAuthWithConversation("/api/auth/phone/login", {
        phone: phoneNumber,
        code: otpCode.join(""),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Verifizierung fehlgeschlagen");
      }

      const session = await installAuthSession(supabase, data.session);
      const isAnonymousUser = session.user.is_anonymous === true;

      finalizeLoginSuccess(isAnonymousUser, data.isNewUser === true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Ungültiger Code. Bitte versuchen Sie es erneut."
      );
    } finally {
      endAuthTransition();
      setIsLoading(false);
    }
  }, [isLoading, phoneNumber, otpCode, supabase, finalizeLoginSuccess]);

  useOtpAutoSubmit({
    code: otpCode,
    isReady: step === "phone-verify",
    isLoading,
    onSubmit: handleOtpVerifyAuto,
  });

  // OAuth handlers
  const handleOAuthLogin = async (provider: "google" | "azure") => {
    setError(null);
    setIsLoading(true);
    const endAuthTransition = beginAuthTransition();

    try {
      const returnPath = getSafeReturnPath(
        returnTo ?? window.location.href,
        window.location.origin
      );
      sessionStorage.setItem(AUTH_REDIRECT_PATH_KEY, returnPath);
      await waitForAnonymousSession();
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          skipBrowserRedirect: true,
        },
      });

      if (oauthError) {
        throw oauthError;
      }
      if (!data.url) throw new Error("Die Anmeldung konnte nicht gestartet werden.");

      // The SDK has now saved the PKCE verifier. Bind the guest conversation to
      // that login attempt on the server before leaving for the provider.
      const response = await fetchAuthWithConversation("/api/auth/prepare-oauth", {});
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.error || "Die Anmeldung konnte nicht vorbereitet werden.");
      }
      window.location.assign(data.url);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
      );
      setIsLoading(false);
    } finally {
      endAuthTransition();
    }
  };

  // Email OTP login handler
  const handleEmailOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: emailOtpEmail.trim(),
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (otpError) {
        if (otpError.message?.toLowerCase().includes("user is banned")) {
          setError("USER_BANNED");
          return;
        }
        throw otpError;
      }

      setStep("email-otp-verify");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleEmailOtpVerifyAuto();
  };

  const handleEmailOtpVerifyAuto = useCallback(async () => {
    if (isLoading) return;
    setError(null);
    setIsLoading(true);
    const endAuthTransition = beginAuthTransition();

    try {
      await waitForAnonymousSession();
      const response = await fetchAuthWithConversation("/api/auth/verify-otp", {
        email: emailOtpEmail.trim(),
        token: emailOtpCode.join(""),
        type: "email",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Verifizierung fehlgeschlagen");
      }

      await installAuthSession(supabase, data.session);

      // A verified guest logging in here is authenticated into their still-
      // anonymous user (no auto-upgrade). finalizeLoginSuccess routes them to
      // the guest-framed dashboard instead of the homepage dead-end.
      finalizeLoginSuccess(data.user?.is_anonymous === true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Ungültiger Code. Bitte versuchen Sie es erneut."
      );
    } finally {
      endAuthTransition();
      setIsLoading(false);
    }
  }, [isLoading, emailOtpEmail, emailOtpCode, supabase, finalizeLoginSuccess]);

  useOtpAutoSubmit({
    code: emailOtpCode,
    isReady: step === "email-otp-verify",
    isLoading,
    onSubmit: handleEmailOtpVerifyAuto,
  });

  const showBackButton = step !== "method" && step !== "success";

  return (
    <div>
      {/* Header */}
      <div className="relative mb-6">
        {showBackButton && (
          <button
            onClick={() => setStep("method")}
            className="absolute left-0 top-0 p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}

        <div className={showBackButton ? "text-center pt-1" : "text-center"}>
          <h2 className="text-xl font-semibold text-zinc-900">
            {step === "success"
              ? "Erfolgreich angemeldet!"
              : (title ?? "Anmelden oder registrieren")}
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            {step === "method" && (subtitle ?? `Willkommen bei ${tenant.name}`)}
            {step === "phone-input" && "Geben Sie Ihre Telefonnummer ein"}
            {step === "phone-verify" && "Geben Sie den Code aus der SMS ein"}
            {step === "email-otp-input" && "Geben Sie Ihre E-Mail-Adresse ein"}
            {step === "email-otp-verify" && "Geben Sie den Code aus der E-Mail ein"}
            {step === "success" && "Sie werden weitergeleitet..."}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && error === "USER_BANNED" ? (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-semibold text-amber-900 mb-1">{t.auth.accountDeactivated}</p>
          <p className="text-sm text-amber-800">{t.auth.accountDeactivatedMessage}</p>
        </div>
      ) : error ? (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* Method Selection */}
      {step === "method" && (
        <div className="space-y-3">
          {/* Mobile Trust Banner */}
          <div className="p-4 rounded-xl mb-2 text-sm bg-tenant-primary/[0.06]">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 flex-shrink-0 mt-0.5 text-tenant-primary" />
              <p className="text-zinc-700">
                <span className="font-medium">Seriöser Marktplatz:</span> Mit Ihrer Anmeldung
                stellen wir sicher, dass nur echte Interessenten miteinander in Kontakt treten.
              </p>
            </div>
          </div>

          {/* Phone Login */}
          <button
            onClick={() => setStep("phone-input")}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-opacity hover:opacity-90 shadow-sm bg-tenant-primary text-tenant-primary-foreground"
          >
            <Phone className="w-5 h-5" />
            <span>Mit Telefonnummer anmelden</span>
          </button>

          {/* Email OTP Login */}
          <button
            onClick={() => setStep("email-otp-input")}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-all border-2 hover:shadow-sm border-tenant-primary text-tenant-primary"
          >
            <Mail className="w-5 h-5" />
            <span>Mit E-Mail anmelden</span>
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-zinc-400">oder</span>
            </div>
          </div>

          {/* Google Login */}
          <button
            onClick={() => handleOAuthLogin("google")}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-zinc-200 rounded-xl hover:bg-zinc-50 transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="font-medium text-zinc-700">Mit Google anmelden</span>
          </button>
        </div>
      )}

      {/* Phone Input */}
      {step === "phone-input" && (
        <form onSubmit={handlePhoneSubmit} className="space-y-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-zinc-700 mb-1">
              Telefonnummer
            </label>
            <PhoneInput
              id="phone"
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder="170 1234567"
              autoFocus
            />
            <p className="text-xs text-zinc-400 mt-1">
              Für den SMS-Versand ist eine Mobilfunknummer erforderlich.
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading || !phoneNumber.trim()}
            className="w-full py-3 text-white rounded-xl font-medium transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 bg-tenant-primary"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sende Code...
              </>
            ) : (
              "Code senden"
            )}
          </button>
        </form>
      )}

      {/* OTP Verification */}
      {step === "phone-verify" && (
        <form onSubmit={handleOtpVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-3 text-center">
              6-stelliger Code
            </label>
            <OtpInput code={otpCode} onChange={setOtpCode} inputsRef={otpInputsRef} autoFocus />
          </div>

          <button
            type="submit"
            disabled={isLoading || otpCode.some((d) => !d)}
            className="w-full py-3 text-white rounded-xl font-medium transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 bg-tenant-primary"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Überprüfe...
              </>
            ) : (
              "Bestätigen"
            )}
          </button>

          <button
            type="button"
            onClick={() => setStep("phone-input")}
            className="w-full py-2 text-sm text-zinc-500 hover:text-zinc-700"
          >
            Code erneut senden
          </button>
        </form>
      )}

      {/* Email OTP Input */}
      {step === "email-otp-input" && (
        <form onSubmit={handleEmailOtpSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email-otp-email"
              className="block text-sm font-medium text-zinc-700 mb-1"
            >
              E-Mail-Adresse
            </label>
            <input
              id="email-otp-email"
              type="email"
              value={emailOtpEmail}
              onChange={(e) => setEmailOtpEmail(e.target.value)}
              placeholder="ihre@email.de"
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tenant-primary focus:border-transparent"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !emailOtpEmail.trim()}
            className="w-full py-3 text-white rounded-xl font-medium transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 bg-tenant-primary"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sende Code...
              </>
            ) : (
              "Code senden"
            )}
          </button>
        </form>
      )}

      {/* Email OTP Verification */}
      {step === "email-otp-verify" && (
        <form onSubmit={handleEmailOtpVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-3 text-center">
              6-stelliger Code
            </label>
            <OtpInput
              code={emailOtpCode}
              onChange={setEmailOtpCode}
              inputsRef={otpInputsRef}
              autoFocus
            />
            <p className="text-sm text-zinc-500 text-center mt-3">
              Code wurde an {emailOtpEmail} gesendet
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading || emailOtpCode.some((d) => !d)}
            className="w-full py-3 text-white rounded-xl font-medium transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 bg-tenant-primary"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Überprüfe...
              </>
            ) : (
              "Bestätigen"
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              setEmailOtpCode(["", "", "", "", "", ""]);
              setStep("email-otp-input");
            }}
            className="w-full py-2 text-sm text-zinc-500 hover:text-zinc-700"
          >
            Code erneut senden
          </button>
        </form>
      )}

      {/* Success */}
      {step === "success" && (
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-tenant-primary/10">
            <CheckCircle className="w-8 h-8 text-tenant-primary" />
          </div>
          {newAccount && (
            <p className="text-sm text-zinc-600">
              Wir haben für diese Nummer ein neues Konto angelegt. Sie suchen Ihr bisheriges Konto?
              Melden Sie sich mit Ihrer E-Mail-Adresse an.
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      {step === "method" && (
        <div className="mt-6 pt-4 border-t border-zinc-100">
          <p className="text-xs text-center text-zinc-400">
            Mit der Anmeldung akzeptieren Sie unsere{" "}
            <Link
              href={routes.content.agb}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-zinc-600"
            >
              AGB
            </Link>{" "}
            und{" "}
            <Link
              href={routes.content.datenschutz}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-zinc-600"
            >
              Datenschutzrichtlinie
            </Link>
          </p>
          <p className="text-center text-sm text-zinc-500 mt-2">
            E-Mail vergessen?{" "}
            <a
              href="mailto:info@oekopunktemarkt.de?subject=Login-Hilfe"
              className="underline hover:text-zinc-700"
            >
              Kontaktieren Sie unseren Support
            </a>
          </p>
        </div>
      )}
    </div>
  );
}
